import * as z from 'zod';

// Action types and features are metadata-driven (served by the
// form-configuration endpoint) — never hardcode them as enums here.

// Embedded action row (SegmentAction entity on the backend)
export const applyFeatureConfigurationActionParametersSchema = z.object({
  featureName: z.string().nullable(),
  configuration: z.record(z.string(), z.unknown()).nullable(),
});
export type ApplyFeatureConfigurationActionParameters = z.infer<
  typeof applyFeatureConfigurationActionParametersSchema
>;

export const segmentActionItemSchema = z.object({
  type: z.string().nullable(),
  applyConfigurationParameters:
    applyFeatureConfigurationActionParametersSchema.nullable(),
});
export type SegmentActionItem = z.infer<typeof segmentActionItemSchema>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readSegmentActionRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type ReadSegmentActionRef = z.infer<typeof readSegmentActionRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const segmentActionRefSchema = z.object({
  id: z.uuid(),
});
export type SegmentActionRef = z.infer<typeof segmentActionRefSchema>;

// 3. Read schema - all properties nullable (API response)
export const readSegmentActionSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  groupName: z.string().nullable(),
  rule: z.string().nullable(),
  actions: z.array(segmentActionItemSchema).nullable(),
  priority: z.number().nullable(),
  enabled: z.boolean().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadSegmentAction = z.infer<typeof readSegmentActionSchema>;

// 4. Create schema
export const createSegmentActionItemSchema = z.object({
  type: z.string().min(1, 'Action type is required'),
  applyConfigurationParameters: z
    .object({
      featureName: z.string().min(1, 'Feature is required'),
      configuration: z.record(z.string(), z.unknown()),
    })
    .optional(),
});
export type CreateSegmentActionItem = z.infer<
  typeof createSegmentActionItemSchema
>;

export const createSegmentActionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  groupName: z.string().optional(),
  rule: z.string().min(1, 'Rule is required'),
  priority: z.number().int('Priority must be an integer').optional(),
  enabled: z.boolean().optional(),
  actions: z.array(createSegmentActionItemSchema).optional(),
});
export type CreateSegmentAction = z.infer<typeof createSegmentActionSchema>;

// 5. Update schema - PUT is a full update; name is included but immutable
// server-side (silently ignored on change)
export const updateSegmentActionSchema = createSegmentActionSchema;
export type UpdateSegmentAction = z.infer<typeof updateSegmentActionSchema>;

// Form-configuration metadata (drives the add/edit form dynamically)
export const featureConfigurationFieldSchema = z.object({
  name: z.string(),
  type: z.string(),
  defaultValue: z.unknown().nullable(),
});
export type FeatureConfigurationField = z.infer<
  typeof featureConfigurationFieldSchema
>;

export const configurableFeatureSchema = z.object({
  name: z.string(),
  configurationSchema: z.array(featureConfigurationFieldSchema).nullable(),
});
export type ConfigurableFeature = z.infer<typeof configurableFeatureSchema>;

export const ruleContextKeySchema = z.object({
  key: z.string(),
  type: z.string().nullable(),
  description: z.string().nullable(),
  example: z.string().nullable(),
});
export type RuleContextKey = z.infer<typeof ruleContextKeySchema>;

export const ruleOperatorSchema = z.object({
  symbol: z.string(),
  description: z.string().nullable(),
  example: z.string().nullable(),
});
export type RuleOperator = z.infer<typeof ruleOperatorSchema>;

export const ruleFunctionSchema = z.object({
  snippet: z.string(),
  description: z.string().nullable(),
  example: z.string().nullable(),
});
export type RuleFunction = z.infer<typeof ruleFunctionSchema>;

export const ruleMetadataSchema = z.object({
  language: z.string().nullable(),
  contextKeys: z.array(ruleContextKeySchema).nullable(),
  operators: z.array(ruleOperatorSchema).nullable(),
  functions: z.array(ruleFunctionSchema).nullable(),
  examples: z.array(z.string()).nullable(),
});
export type RuleMetadata = z.infer<typeof ruleMetadataSchema>;

export const readSegmentActionFormConfigurationSchema = z.object({
  actionTypes: z.array(z.string()).nullable(),
  features: z.array(configurableFeatureSchema).nullable(),
  rule: ruleMetadataSchema.nullable(),
});
export type ReadSegmentActionFormConfiguration = z.infer<
  typeof readSegmentActionFormConfigurationSchema
>;

// Legacy exports
export const segmentActionSchema = readSegmentActionSchema;
export type SegmentAction = ReadSegmentAction;
